import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

const STORAGE_KEY = 'analyticsSidebarCollapsed'
const MOBILE_BREAKPOINT = 768 // Bootstrap "md" breakpoint

const noop = () => {}

const SidebarContext = createContext({
  isCollapsed: false,
  toggleCollapsed: noop,
  mobileOpen: false,
  openMobile: noop,
  closeMobile: noop,
  isMobile: false
})

function readInitialCollapsed() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch (_e) {
    return false
  }
}

function readInitialIsMobile() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`).matches
}

export function SidebarProvider({ children }) {
  const [isCollapsed, setIsCollapsed] = useState(readInitialCollapsed)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(readInitialIsMobile)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(isCollapsed))
    } catch (_e) {
      /* ignore storage failures */
    }
  }, [isCollapsed])

  // Track viewport size so we know when we're in the mobile (offcanvas) mode.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`)
    const handler = (event) => setIsMobile(event.matches)
    setIsMobile(mql.matches)
    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
      return () => mql.removeEventListener('change', handler)
    }
    // Safari < 14 fallback
    mql.addListener(handler)
    return () => mql.removeListener(handler)
  }, [])

  // If the user resizes back up to desktop while the mobile drawer is open,
  // close it so the sidebar doesn't render as both a drawer and a sticky column.
  useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false)
  }, [isMobile, mobileOpen])

  const toggleCollapsed = useCallback(() => setIsCollapsed((v) => !v), [])
  const openMobile = useCallback(() => setMobileOpen(true), [])
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  return (
    <SidebarContext.Provider
      value={{ isCollapsed, toggleCollapsed, mobileOpen, openMobile, closeMobile, isMobile }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

SidebarProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export function useSidebar() {
  return useContext(SidebarContext)
}

export default SidebarContext
